import { rgbToAciColor } from './aci-colors';
import { DxfColor, DxfPoint } from './types';

const DEFAULT_COLOR_INDEX = 7;

interface LayerDefinition {
  name: string;
  colorIndex: number;
}

/**
 * Low-level DXF AC1015 (AutoCAD 2000) document builder.
 *
 * Handles the full AC1015 structure: HEADER, CLASSES, TABLES, BLOCKS,
 * ENTITIES, and OBJECTS sections, with proper entity handles, owner
 * handles (330), and subclass markers throughout.
 *
 * PlotStyleName uses ACDBDICTIONARYWDFLT + ACDBPLACEHOLDER structure
 * matching what ODA-based readers (e.g. Pythagoras) expect.
 */
export class DxfWriter {
  private handleCounter = 1;
  private lines: string[] = [];

  /** Owner (330) for entity writers: model space, or the open block's record. */
  private currentOwnerHandle: string;
  private readonly blockRecordHandles = new Map<string, string>();
  private openBlockName: string | null = null;

  // Pre-allocated handles for cross-referenced objects
  private readonly rootDictHandle: string;
  private readonly acadGroupDictHandle: string;
  private readonly plotStyleNameDictHandle: string;
  private readonly plotStyleNamePlaceholderHandle: string;
  private readonly vportTableHandle: string;
  private readonly ltypeTableHandle: string;
  private readonly layerTableHandle: string;
  private readonly styleTableHandle: string;
  private readonly viewTableHandle: string;
  private readonly ucsTableHandle: string;
  private readonly appIdTableHandle: string;
  private readonly dimStyleTableHandle: string;
  private readonly blockRecordTableHandle: string;
  private readonly modelSpaceBlockRecordHandle: string;
  private readonly paperSpaceBlockRecordHandle: string;

  constructor() {
    // Reserve handles for objects that need cross-referencing.
    // Order matters — these get the lowest handle numbers.
    this.rootDictHandle = this.nextHandle();
    this.acadGroupDictHandle = this.nextHandle();
    this.plotStyleNameDictHandle = this.nextHandle();
    this.plotStyleNamePlaceholderHandle = this.nextHandle();
    this.vportTableHandle = this.nextHandle();
    this.ltypeTableHandle = this.nextHandle();
    this.layerTableHandle = this.nextHandle();
    this.styleTableHandle = this.nextHandle();
    this.viewTableHandle = this.nextHandle();
    this.ucsTableHandle = this.nextHandle();
    this.appIdTableHandle = this.nextHandle();
    this.dimStyleTableHandle = this.nextHandle();
    this.blockRecordTableHandle = this.nextHandle();
    this.modelSpaceBlockRecordHandle = this.nextHandle();
    this.paperSpaceBlockRecordHandle = this.nextHandle();
    this.currentOwnerHandle = this.modelSpaceBlockRecordHandle;
  }

  /**
   * Reserve a BLOCK_RECORD handle per block name. Must be called before
   * writeTables so the records appear in the BLOCK_RECORD table.
   */
  allocateBlockRecords(names: string[]): void {
    for (const name of names) {
      if (!this.blockRecordHandles.has(name)) {
        this.blockRecordHandles.set(name, this.nextHandle());
      }
    }
  }

  private nextHandle(): string {
    return (this.handleCounter++).toString(16).toUpperCase();
  }

  private addLine(value: string | number): void {
    this.lines.push(String(value));
  }

  private addPair(groupCode: number, value: string | number): void {
    this.addLine(groupCode);
    this.addLine(value);
  }

  // DXF is line-based: every group code and value occupies its own physical
  // line. A string value containing a newline would split into bogus extra
  // lines and corrupt the whole document, so collapse any newline to a space.
  private sanitizeText(text: string): string {
    return text.replace(/[\r\n]+/g, ' ');
  }

  // A number is taken as an explicit ACI index (0 = BYBLOCK, 256 = BYLAYER).
  private colorToAci(color: DxfColor | number | undefined): number {
    if (color === undefined) return DEFAULT_COLOR_INDEX;
    if (typeof color === 'number') return color;
    return rgbToAciColor(color.r, color.g, color.b);
  }

  // --- Sections ---

  writeHeader(extMin: DxfPoint, extMax: DxfPoint, insUnits = 6): void {
    this.addPair(0, 'SECTION');
    this.addPair(2, 'HEADER');

    this.addPair(9, '$ACADVER');
    this.addPair(1, 'AC1015');

    this.addPair(9, '$INSBASE');
    this.addPair(10, 0.0);
    this.addPair(20, 0.0);
    this.addPair(30, 0.0);

    this.addPair(9, '$EXTMIN');
    this.addPair(10, extMin.x);
    this.addPair(20, extMin.y);
    this.addPair(30, extMin.z);

    this.addPair(9, '$EXTMAX');
    this.addPair(10, extMax.x);
    this.addPair(20, extMax.y);
    this.addPair(30, extMax.z);

    this.addPair(9, '$INSUNITS');
    this.addPair(70, insUnits);

    this.addPair(9, '$CLAYER');
    this.addPair(8, '0');

    this.addPair(9, '$HANDSEED');
    this.addPair(5, 'FFFF');

    this.addPair(0, 'ENDSEC');
  }

  writeClasses(): void {
    this.addPair(0, 'SECTION');
    this.addPair(2, 'CLASSES');

    this.addPair(0, 'CLASS');
    this.addPair(1, 'ACDBDICTIONARYWDFLT');
    this.addPair(2, 'AcDbDictionaryWithDefault');
    this.addPair(3, 'ObjectDBX Classes');
    this.addPair(90, 0);
    this.addPair(280, 0);
    this.addPair(281, 0);

    this.addPair(0, 'CLASS');
    this.addPair(1, 'ACDBPLACEHOLDER');
    this.addPair(2, 'AcDbPlaceHolder');
    this.addPair(3, 'ObjectDBX Classes');
    this.addPair(90, 0);
    this.addPair(280, 0);
    this.addPair(281, 0);

    this.addPair(0, 'ENDSEC');
  }

  writeTables(layers: LayerDefinition[]): void {
    this.addPair(0, 'SECTION');
    this.addPair(2, 'TABLES');

    this.writeVportTable();
    this.writeLinetypeTable();
    this.writeLayerTable(layers);
    this.writeStyleTable();
    this.writeViewTable();
    this.writeUcsTable();
    this.writeAppIdTable();
    this.writeDimStyleTable();
    this.writeBlockRecordTable();

    this.addPair(0, 'ENDSEC');
  }

  writeBlocksStart(): void {
    this.addPair(0, 'SECTION');
    this.addPair(2, 'BLOCKS');

    // *MODEL_SPACE
    this.addPair(0, 'BLOCK');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.modelSpaceBlockRecordHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, '0');
    this.addPair(100, 'AcDbBlockBegin');
    this.addPair(2, '*MODEL_SPACE');
    this.addPair(70, 0);
    this.addPair(10, 0.0);
    this.addPair(20, 0.0);
    this.addPair(30, 0.0);
    this.addPair(3, '*MODEL_SPACE');
    this.addPair(1, '');

    this.addPair(0, 'ENDBLK');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.modelSpaceBlockRecordHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, '0');
    this.addPair(100, 'AcDbBlockEnd');

    // *PAPER_SPACE
    this.addPair(0, 'BLOCK');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.paperSpaceBlockRecordHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, '0');
    this.addPair(100, 'AcDbBlockBegin');
    this.addPair(2, '*PAPER_SPACE');
    this.addPair(70, 0);
    this.addPair(10, 0.0);
    this.addPair(20, 0.0);
    this.addPair(30, 0.0);
    this.addPair(3, '*PAPER_SPACE');
    this.addPair(1, '');

    this.addPair(0, 'ENDBLK');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.paperSpaceBlockRecordHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, '0');
    this.addPair(100, 'AcDbBlockEnd');
  }

  writeBlocksEnd(): void {
    this.addPair(0, 'ENDSEC');
  }

  /**
   * Open a user block definition. Entity writers called until endBlock() emit
   * into this block (their owner handle becomes the block's BLOCK_RECORD).
   * The name must have been passed to allocateBlockRecords first.
   */
  beginBlock(name: string, basePoint?: DxfPoint): void {
    const recordHandle = this.blockRecordHandles.get(name);
    if (!recordHandle) throw new Error(`Block record not allocated for block "${name}"`);
    if (this.openBlockName !== null) throw new Error(`Cannot begin block "${name}" inside open block "${this.openBlockName}"`);
    this.openBlockName = name;

    this.addPair(0, 'BLOCK');
    this.addPair(5, this.nextHandle());
    this.addPair(330, recordHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, '0');
    this.addPair(100, 'AcDbBlockBegin');
    this.addPair(2, name);
    this.addPair(70, 0);
    this.addPair(10, basePoint?.x ?? 0.0);
    this.addPair(20, basePoint?.y ?? 0.0);
    this.addPair(30, basePoint?.z ?? 0.0);
    this.addPair(3, name);
    this.addPair(1, '');

    this.currentOwnerHandle = recordHandle;
  }

  endBlock(): void {
    if (this.openBlockName === null) throw new Error('endBlock() without an open block');
    const recordHandle = this.blockRecordHandles.get(this.openBlockName);

    this.addPair(0, 'ENDBLK');
    this.addPair(5, this.nextHandle());
    this.addPair(330, recordHandle as string);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, '0');
    this.addPair(100, 'AcDbBlockEnd');

    this.openBlockName = null;
    this.currentOwnerHandle = this.modelSpaceBlockRecordHandle;
  }

  writeEntitiesStart(): void {
    this.addPair(0, 'SECTION');
    this.addPair(2, 'ENTITIES');
  }

  writeEntitiesEnd(): void {
    this.addPair(0, 'ENDSEC');
  }

  writeObjects(): void {
    this.addPair(0, 'SECTION');
    this.addPair(2, 'OBJECTS');

    // Root dictionary
    this.addPair(0, 'DICTIONARY');
    this.addPair(5, this.rootDictHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbDictionary');
    this.addPair(281, 1);
    this.addPair(3, 'ACAD_GROUP');
    this.addPair(350, this.acadGroupDictHandle);
    this.addPair(3, 'ACAD_PLOTSTYLENAME');
    this.addPair(350, this.plotStyleNameDictHandle);

    // ACAD_GROUP dictionary (empty, but required by many readers)
    this.addPair(0, 'DICTIONARY');
    this.addPair(5, this.acadGroupDictHandle);
    this.addPair(330, this.rootDictHandle);
    this.addPair(100, 'AcDbDictionary');
    this.addPair(281, 1);

    // PlotStyleName dictionary-with-default (ACDBDICTIONARYWDFLT)
    // Contains a "Normal" entry pointing to the ACDBPLACEHOLDER.
    this.addPair(0, 'ACDBDICTIONARYWDFLT');
    this.addPair(5, this.plotStyleNameDictHandle);
    this.addPair(330, this.rootDictHandle);
    this.addPair(100, 'AcDbDictionary');
    this.addPair(281, 1);
    this.addPair(3, 'Normal');
    this.addPair(350, this.plotStyleNamePlaceholderHandle);
    this.addPair(100, 'AcDbDictionaryWithDefault');
    this.addPair(340, this.plotStyleNamePlaceholderHandle);

    // The actual "Normal" plot style placeholder — layers point here via 390
    this.addPair(0, 'ACDBPLACEHOLDER');
    this.addPair(5, this.plotStyleNamePlaceholderHandle);
    this.addPair(330, this.plotStyleNameDictHandle);

    this.addPair(0, 'ENDSEC');
  }

  writeEof(): void {
    this.addPair(0, 'EOF');
  }

  // --- Entity writers ---

  writePoint(layerName: string, color: DxfColor | number | undefined, position: DxfPoint): void {
    this.addPair(0, 'POINT');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(6, 'Continuous');
    this.addPair(62, this.colorToAci(color));
    this.addPair(100, 'AcDbPoint');
    this.addPair(10, position.x);
    this.addPair(20, position.y);
    this.addPair(30, position.z);
  }

  writeLine(layerName: string, color: DxfColor | number | undefined, start: DxfPoint, end: DxfPoint): void {
    this.addPair(0, 'LINE');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(6, 'Continuous');
    this.addPair(62, this.colorToAci(color));
    this.addPair(100, 'AcDbLine');
    this.addPair(10, start.x);
    this.addPair(20, start.y);
    this.addPair(30, start.z);
    this.addPair(11, end.x);
    this.addPair(21, end.y);
    this.addPair(31, end.z);
  }

  writeInsert(layerName: string, color: DxfColor | number | undefined, blockName: string, position: DxfPoint, rotationDeg = 0, xScale = 1, yScale = 1, zScale = 1): void {
    this.addPair(0, 'INSERT');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(62, this.colorToAci(color));
    this.addPair(100, 'AcDbBlockReference');
    this.addPair(2, blockName);
    this.addPair(10, position.x);
    this.addPair(20, position.y);
    this.addPair(30, position.z);
    this.addPair(41, xScale);
    this.addPair(42, yScale);
    this.addPair(43, zScale);
    this.addPair(50, rotationDeg);
  }

  writeText(layerName: string, color: DxfColor | number | undefined, position: DxfPoint, text: string, height: number, rotation = 0, hAlign = 0, vAlign = 0): void {
    this.addPair(0, 'TEXT');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(62, this.colorToAci(color));
    this.addPair(100, 'AcDbText');
    this.addPair(10, position.x);
    this.addPair(20, position.y);
    this.addPair(30, position.z);
    this.addPair(40, height);
    this.addPair(1, this.sanitizeText(text));
    if (rotation) this.addPair(50, rotation);

    // When justified, the alignment point (11/21/31) drives placement and must
    // mirror the insertion point. Group 72 precedes the second subclass marker,
    // group 73 follows it.
    const justified = hAlign !== 0 || vAlign !== 0;
    if (justified) {
      this.addPair(72, hAlign);
      this.addPair(11, position.x);
      this.addPair(21, position.y);
      this.addPair(31, position.z);
    }
    this.addPair(100, 'AcDbText');
    if (justified) this.addPair(73, vAlign);
  }

  writePolyline(layerName: string, color: DxfColor | number | undefined, points: DxfPoint[], closed: boolean, dashed = false, bulges?: number[]): void {
    // 3D polylines (AcDb3dPolyline) always render solid in AutoCAD/ODA readers
    // — they ignore non-continuous linetypes, and their vertices cannot carry
    // bulge (group 42). Dashed or bulged polylines must therefore be planar 2D
    // LWPOLYLINEs at a single elevation.
    const hasBulges = !!bulges?.some((b) => b !== 0);
    if (dashed || hasBulges) {
      this.writeLwPolyline(layerName, color, points, closed, dashed ? 'DASHED' : 'Continuous', hasBulges ? bulges : undefined);
      return;
    }

    const aciColor = this.colorToAci(color);
    let flags = 8; // 3D polyline
    if (closed) flags += 1;

    const polylineHandle = this.nextHandle();

    // POLYLINE header — owned by model space or the open block
    this.addPair(0, 'POLYLINE');
    this.addPair(5, polylineHandle);
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(6, dashed ? 'DASHED' : 'Continuous');
    this.addPair(62, aciColor);
    this.addPair(100, 'AcDb3dPolyline');
    this.addPair(66, 1); // vertices follow
    this.addPair(10, 0.0);
    this.addPair(20, 0.0);
    this.addPair(30, 0.0);
    this.addPair(70, flags);

    // VERTEX entries — owned by the POLYLINE
    for (const point of points) {
      this.addPair(0, 'VERTEX');
      this.addPair(5, this.nextHandle());
      this.addPair(330, polylineHandle);
      this.addPair(100, 'AcDbEntity');
      this.addPair(8, layerName);
      this.addPair(100, 'AcDbVertex');
      this.addPair(100, 'AcDb3dPolylineVertex');
      this.addPair(10, point.x);
      this.addPair(20, point.y);
      this.addPair(30, point.z);
      this.addPair(70, 32);
    }

    // SEQEND — owned by the POLYLINE
    this.addPair(0, 'SEQEND');
    this.addPair(5, this.nextHandle());
    this.addPair(330, polylineHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
  }

  // Planar 2D polyline at a single elevation. Unlike a 3D polyline, an
  // LWPOLYLINE honors non-continuous linetypes (group 6) and per-vertex arc
  // bulges (group 42; bulges[i] curves points[i] → points[i+1]). Z is taken
  // from the first vertex; all vertices are assumed coplanar (callers flatten
  // first).
  private writeLwPolyline(layerName: string, color: DxfColor | number | undefined, points: DxfPoint[], closed: boolean, linetype: string, bulges?: number[]): void {
    const aciColor = this.colorToAci(color);
    const elevation = points.length > 0 ? points[0].z : 0;

    this.addPair(0, 'LWPOLYLINE');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(6, linetype);
    this.addPair(62, aciColor);
    this.addPair(100, 'AcDbPolyline');
    this.addPair(90, points.length);
    this.addPair(70, closed ? 1 : 0);
    this.addPair(38, elevation);
    for (let i = 0; i < points.length; i++) {
      this.addPair(10, points[i].x);
      this.addPair(20, points[i].y);
      // Omitted 42 defaults to 0 (straight) per the DXF spec. An open
      // polyline has no closing segment, so its last vertex carries no bulge.
      if (bulges && bulges[i] && (closed || i < points.length - 1)) this.addPair(42, bulges[i]);
    }
  }

  writeHatch(layerName: string, color: DxfColor | number | undefined, outerBoundary: DxfPoint[], islands: DxfPoint[][]): void {
    const aciColor = this.colorToAci(color);
    const elevation = outerBoundary.length > 0 ? outerBoundary[0].z : 0;
    const totalPaths = 1 + islands.length;

    this.addPair(0, 'HATCH');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.currentOwnerHandle);
    this.addPair(100, 'AcDbEntity');
    this.addPair(8, layerName);
    this.addPair(62, aciColor);
    this.addPair(100, 'AcDbHatch');

    // Elevation point
    this.addPair(10, 0.0);
    this.addPair(20, 0.0);
    this.addPair(30, elevation);

    // Extrusion direction (normal to XY plane)
    this.addPair(210, 0.0);
    this.addPair(220, 0.0);
    this.addPair(230, 1.0);

    // Pattern
    this.addPair(2, 'SOLID');
    this.addPair(70, 1); // solid fill
    this.addPair(71, 0); // non-associative

    // Boundary paths
    this.addPair(91, totalPaths);

    // Outer boundary (flags: 1=External + 2=Polyline = 3)
    const outerPoints = DxfWriter.ensureWinding(outerBoundary, true);
    this.writeHatchBoundaryPath(outerPoints, 3);

    // Island boundaries (flags: 2=Polyline + 16=Outermost = 18)
    for (const island of islands) {
      const islandPoints = DxfWriter.ensureWinding(island, false);
      this.writeHatchBoundaryPath(islandPoints, 18);
    }

    // Hatch footer
    this.addPair(75, 0); // hatch style: nested
    this.addPair(76, 1); // pattern type: predefined
    this.addPair(98, 0); // no seed points
  }

  private writeHatchBoundaryPath(points: DxfPoint[], pathTypeFlags: number): void {
    this.addPair(92, pathTypeFlags);
    this.addPair(72, 0); // no bulge
    this.addPair(73, 1); // closed
    this.addPair(93, points.length);
    for (const p of points) {
      this.addPair(10, p.x);
      this.addPair(20, p.y);
    }
    this.addPair(97, 0); // number of source boundary objects
  }

  private static computeSignedArea2D(points: DxfPoint[]): number {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return area / 2;
  }

  private static ensureWinding(points: DxfPoint[], ccw: boolean): DxfPoint[] {
    const signedArea = DxfWriter.computeSignedArea2D(points);
    const isCcw = signedArea > 0;
    if (isCcw !== ccw) {
      return [...points].reverse();
    }
    return points;
  }

  // --- Table helpers ---

  private writeVportTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'VPORT');
    this.addPair(5, this.vportTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 0);
    this.addPair(0, 'ENDTAB');
  }

  private writeLinetypeTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'LTYPE');
    this.addPair(5, this.ltypeTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 4);

    // ByBlock
    this.addPair(0, 'LTYPE');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.ltypeTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbLinetypeTableRecord');
    this.addPair(2, 'ByBlock');
    this.addPair(70, 0);
    this.addPair(3, '');
    this.addPair(72, 65);
    this.addPair(73, 0);
    this.addPair(40, 0.0);

    // ByLayer
    this.addPair(0, 'LTYPE');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.ltypeTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbLinetypeTableRecord');
    this.addPair(2, 'ByLayer');
    this.addPair(70, 0);
    this.addPair(3, '');
    this.addPair(72, 65);
    this.addPair(73, 0);
    this.addPair(40, 0.0);

    // Continuous
    this.addPair(0, 'LTYPE');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.ltypeTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbLinetypeTableRecord');
    this.addPair(2, 'Continuous');
    this.addPair(70, 0);
    this.addPair(3, 'Solid line');
    this.addPair(72, 65);
    this.addPair(73, 0);
    this.addPair(40, 0.0);

    // Dashed — 0.2 dash, 0.1 gap (drawing units). Used by entities that set
    // group 6 to 'DASHED' (e.g. structures); overrides their layer's linetype.
    this.addPair(0, 'LTYPE');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.ltypeTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbLinetypeTableRecord');
    this.addPair(2, 'DASHED');
    this.addPair(70, 0);
    this.addPair(3, 'Dashed __ __ __ __ __ __ __ __');
    this.addPair(72, 65);
    this.addPair(73, 2); // number of dash-length elements
    this.addPair(40, 0.3); // total pattern length
    this.addPair(49, 0.2); // dash (on)
    this.addPair(74, 0);
    this.addPair(49, -0.1); // gap (off)
    this.addPair(74, 0);

    this.addPair(0, 'ENDTAB');
  }

  private writeLayerTable(layers: LayerDefinition[]): void {
    const allLayers: LayerDefinition[] = [{ name: '0', colorIndex: DEFAULT_COLOR_INDEX }];
    const seen = new Set<string>(['0']);
    for (const layer of layers) {
      if (!seen.has(layer.name)) {
        allLayers.push(layer);
        seen.add(layer.name);
      }
    }

    this.addPair(0, 'TABLE');
    this.addPair(2, 'LAYER');
    this.addPair(5, this.layerTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, allLayers.length);

    for (const layer of allLayers) {
      this.addPair(0, 'LAYER');
      this.addPair(5, this.nextHandle());
      this.addPair(330, this.layerTableHandle);
      this.addPair(100, 'AcDbSymbolTableRecord');
      this.addPair(100, 'AcDbLayerTableRecord');
      this.addPair(2, layer.name);
      this.addPair(70, 0);
      this.addPair(62, layer.colorIndex);
      this.addPair(6, 'Continuous');
      this.addPair(370, -3);
      // 390 must point to the ACDBPLACEHOLDER inside the PlotStyleName dict
      this.addPair(390, this.plotStyleNamePlaceholderHandle);
    }

    this.addPair(0, 'ENDTAB');
  }

  private writeStyleTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'STYLE');
    this.addPair(5, this.styleTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 0);
    this.addPair(0, 'ENDTAB');
  }

  private writeViewTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'VIEW');
    this.addPair(5, this.viewTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 0);
    this.addPair(0, 'ENDTAB');
  }

  private writeUcsTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'UCS');
    this.addPair(5, this.ucsTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 0);
    this.addPair(0, 'ENDTAB');
  }

  private writeDimStyleTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'DIMSTYLE');
    this.addPair(5, this.dimStyleTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 0);
    this.addPair(100, 'AcDbDimStyleTable');
    this.addPair(0, 'ENDTAB');
  }

  private writeAppIdTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'APPID');
    this.addPair(5, this.appIdTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 1);

    this.addPair(0, 'APPID');
    this.addPair(5, this.nextHandle());
    this.addPair(330, this.appIdTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbRegAppTableRecord');
    this.addPair(2, 'ACAD');
    this.addPair(70, 0);

    this.addPair(0, 'ENDTAB');
  }

  private writeBlockRecordTable(): void {
    this.addPair(0, 'TABLE');
    this.addPair(2, 'BLOCK_RECORD');
    this.addPair(5, this.blockRecordTableHandle);
    this.addPair(330, '0');
    this.addPair(100, 'AcDbSymbolTable');
    this.addPair(70, 2 + this.blockRecordHandles.size);

    this.addPair(0, 'BLOCK_RECORD');
    this.addPair(5, this.modelSpaceBlockRecordHandle);
    this.addPair(330, this.blockRecordTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbBlockTableRecord');
    this.addPair(2, '*MODEL_SPACE');

    this.addPair(0, 'BLOCK_RECORD');
    this.addPair(5, this.paperSpaceBlockRecordHandle);
    this.addPair(330, this.blockRecordTableHandle);
    this.addPair(100, 'AcDbSymbolTableRecord');
    this.addPair(100, 'AcDbBlockTableRecord');
    this.addPair(2, '*PAPER_SPACE');

    for (const [name, handle] of this.blockRecordHandles) {
      this.addPair(0, 'BLOCK_RECORD');
      this.addPair(5, handle);
      this.addPair(330, this.blockRecordTableHandle);
      this.addPair(100, 'AcDbSymbolTableRecord');
      this.addPair(100, 'AcDbBlockTableRecord');
      this.addPair(2, name);
    }

    this.addPair(0, 'ENDTAB');
  }

  // --- Output ---

  toString(): string {
    return this.lines.join('\r\n');
  }
}
